import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import RightModal from "../components/templates/RightModal";
import { getAgentsList, selectAgentsData, selectAgentsLoading } from "../redux/agents/agentsSlice";
import { resetRefreshNow, selectRightModalId, selectRightModalIsOpen, selectRightModalIsRefreshNow, selectRightModalmode, setModal } from "../redux/rightModalSlice";
import DynamicIcon from "../components/DynamicIcon";
import LoadingSection from "../components/LoadingSection";
import AddEditAgent from "../components/agentsPage/AddEditAgent";
import DeleteAgentConfirmation from "../components/agentsPage/DeleteAgentConfirmation";
import AgentCredentials from "../components/agentsPage/AgentCredentials";
import { useColorPalette } from '../hooks/useColorPalette';
import DataTable from "../components/DataTable/DataTable";

export default function AgentsList() {
    const dispatch = useDispatch();
    const palette = useColorPalette();
    const agentsList = useSelector(selectAgentsData);
    const agentsLoading = useSelector(selectAgentsLoading);
    const token = localStorage.getItem('installamelo-auth-token');
    const [dataTableKey, setDataTableKey] = useState(Date.now());

    // RightModal gestita tramite Redux
    const rightModalIsOpen = useSelector(selectRightModalIsOpen);
    const rightModalMode = useSelector(selectRightModalmode);
    const rightModalAgentId = useSelector(selectRightModalId);

    // Caricamento iniziale
    useEffect(() => {
        dispatch(getAgentsList({ token }));
        dispatch(resetRefreshNow());
    }, [dispatch, token]);

    // Refresh dopo operazioni modal
    const rightModalIsRefreshNow = useSelector(selectRightModalIsRefreshNow);
    useEffect(() => {
        if (rightModalIsRefreshNow) {
            dispatch(getAgentsList({ token }));
            dispatch(resetRefreshNow());
            setDataTableKey(Date.now());
        }
    }, [rightModalIsRefreshNow]);

    // Aggiorna la tabella quando il modal viene chiuso
    useEffect(() => {
        if (!rightModalIsOpen && dataTableKey) {
            setDataTableKey(Date.now());
        }
    }, [rightModalIsOpen]);

    const truncateKey = (key) => {
        if (!key) return '-';
        if (key.length <= 16) return key;
        return key.substring(0, 16) + '...';
    };

    return (
        <div className="page">
            {agentsLoading && <LoadingSection isFixed />}
            <h1><span className="font-light">Impostazioni / </span>Agenti AI</h1>
            <div className="box">

                {/* Nuovo agente */}
                <div className="flex justify-end mb-4">
                    <button className="primary" onClick={() => dispatch(setModal({ mode: 'add' }))}>
                        <DynamicIcon iconClass="fa-solid fa-plus" /> Nuovo Agente
                    </button>
                </div>

                {/* DataTable */}
                <div className="w-full my-4 overflow-x-auto">
                    <DataTable
                        key={dataTableKey}
                        columns={[
                            {
                                id: 'agentName',
                                label: 'NOME',
                                width: '20%',
                                render: (agent) => (
                                    <div className="text-left">{agent.agentName || '-'}</div>
                                )
                            },
                            {
                                id: 'agentType',
                                label: 'TIPO',
                                width: '12%',
                                render: (agent) => (
                                    <div className="text-left uppercase">{agent.agentType || '-'}</div>
                                )
                            },
                            {
                                id: 'agentKey',
                                label: 'KEY',
                                width: '20%',
                                render: (agent) => (
                                    <div className="text-left font-mono text-xs">{truncateKey(agent.agentKey)}</div>
                                )
                            },
                            {
                                id: 'permissions',
                                label: 'PERMESSI',
                                width: '12%',
                                render: (agent) => (
                                    <div className="text-center">
                                        {agent.permissions?.includes('*')
                                            ? <span className="text-xs font-medium px-2 py-0.5 rounded bg-purple-100 text-purple-700">Tutti</span>
                                            : <span className="text-xs font-medium">{agent.permissions?.length || 0}</span>
                                        }
                                    </div>
                                )
                            },
                            {
                                id: 'active',
                                label: 'STATO',
                                width: '12%',
                                render: (agent) => (
                                    <div className="text-center">
                                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                                            agent.active !== false
                                                ? 'bg-green-100 text-green-700'
                                                : 'bg-red-100 text-red-700'
                                        }`}>
                                            {agent.active !== false ? 'Attivo' : 'Disattivato'}
                                        </span>
                                    </div>
                                )
                            },
                            {
                                id: 'actions',
                                label: 'OPZ',
                                width: '14%',
                                render: (agent) => (
                                    <div className="flex gap-3 justify-center">
                                        <button
                                            style={{ color: 'inherit' }}
                                            title="Credenziali"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                dispatch(setModal({ isOpen: true, mode: 'credentials', id: agent._id }));
                                            }}
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-key" />
                                        </button>
                                        <button
                                            style={{ color: 'inherit' }}
                                            title="Modifica"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                dispatch(setModal({ isOpen: true, mode: 'edit', id: agent._id }));
                                            }}
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-edit" />
                                        </button>
                                        <button
                                            style={{ color: 'inherit' }}
                                            title="Elimina"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                dispatch(setModal({ id: agent._id, mode: 'delete' }));
                                            }}
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-trash" />
                                        </button>
                                    </div>
                                )
                            }
                        ]}
                        data={agentsList || []}
                        loading={agentsLoading}
                        totalRecords={agentsList?.length || 0}
                        pageSize={10}
                        emptyMessage="Nessun agente trovato"
                        renderCustomRow={(agent, index, isEven, tableStyles) => {
                            const handleRowClick = () => {
                                dispatch(setModal({ isOpen: true, mode: 'edit', id: agent._id }));
                            };

                            return (
                                <div
                                    key={agent._id}
                                    className="data-table-row cursor-pointer"
                                    style={{
                                        backgroundColor: isEven ? tableStyles.evenRow.backgroundColor : tableStyles.oddRow.backgroundColor,
                                        color: isEven ? tableStyles.evenRow.color : tableStyles.oddRow.color,
                                    }}
                                    onClick={handleRowClick}
                                >
                                    {/* NOME */}
                                    <div className="data-table-cell" style={{ width: '20%' }} data-label="Nome">
                                        <div className="text-left">{agent.agentName || '-'}</div>
                                    </div>

                                    {/* TIPO */}
                                    <div className="data-table-cell" style={{ width: '12%' }} data-label="Tipo">
                                        <div className="text-left uppercase">{agent.agentType || '-'}</div>
                                    </div>

                                    {/* KEY */}
                                    <div className="data-table-cell" style={{ width: '20%' }} data-label="Key">
                                        <div className="text-left font-mono text-xs">{truncateKey(agent.agentKey)}</div>
                                    </div>

                                    {/* PERMESSI */}
                                    <div className="data-table-cell" style={{ width: '12%' }} data-label="Permessi">
                                        <div className="text-center">
                                            {agent.permissions?.includes('*')
                                                ? <span className="text-xs font-medium px-2 py-0.5 rounded bg-purple-100 text-purple-700">Tutti</span>
                                                : <span className="text-xs font-medium">{agent.permissions?.length || 0}</span>
                                            }
                                        </div>
                                    </div>

                                    {/* STATO */}
                                    <div className="data-table-cell" style={{ width: '12%' }} data-label="Stato">
                                        <div className="text-center">
                                            <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                                                agent.active !== false
                                                    ? 'bg-green-100 text-green-700'
                                                    : 'bg-red-100 text-red-700'
                                            }`}>
                                                {agent.active !== false ? 'Attivo' : 'Disattivato'}
                                            </span>
                                        </div>
                                    </div>

                                    {/* AZIONI */}
                                    <div className="data-table-cell" style={{ width: '14%' }} data-label="Azioni">
                                        <div className="flex gap-3 justify-center">
                                            <button
                                                style={{ color: isEven ? tableStyles.evenRow.color : tableStyles.oddRow.color }}
                                                title="Credenziali"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    dispatch(setModal({ isOpen: true, mode: 'credentials', id: agent._id }));
                                                }}
                                            >
                                                <DynamicIcon iconClass="fa-solid fa-key" />
                                            </button>
                                            <button
                                                style={{ color: isEven ? tableStyles.evenRow.color : tableStyles.oddRow.color }}
                                                title="Modifica"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    dispatch(setModal({ isOpen: true, mode: 'edit', id: agent._id }));
                                                }}
                                            >
                                                <DynamicIcon iconClass="fa-solid fa-edit" />
                                            </button>
                                            <button
                                                style={{ color: isEven ? tableStyles.evenRow.color : tableStyles.oddRow.color }}
                                                title="Elimina"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    dispatch(setModal({ id: agent._id, mode: 'delete' }));
                                                }}
                                            >
                                                <DynamicIcon iconClass="fa-solid fa-trash" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        }}
                    />
                </div>
            </div>

            {/* RIGHT MODAL */}
            {rightModalIsOpen && (
                <RightModal>
                    {(rightModalMode === 'add' || rightModalMode === 'edit') && (
                        <AddEditAgent agentId={rightModalAgentId} />
                    )}
                    {rightModalMode === 'delete' && (
                        <DeleteAgentConfirmation
                            agentId={rightModalAgentId}
                            agentName={agentsList?.find(a => a._id === rightModalAgentId)?.agentName || ""}
                        />
                    )}
                    {rightModalMode === 'credentials' && (
                        <AgentCredentials
                            agentId={rightModalAgentId}
                            agentName={agentsList?.find(a => a._id === rightModalAgentId)?.agentName || ""}
                            agentKey={agentsList?.find(a => a._id === rightModalAgentId)?.agentKey || ""}
                        />
                    )}
                </RightModal>
            )}
        </div>
    );
}
